import { useLayoutEffect, useMemo, useRef, useState } from "react";

export const PV_EST_HEADER_BACKGROUND_LIGHT = "var(--pv-table-header-bg)";

export const getPvEstimationHeaderBackground = (theme) =>
  theme.palette.mode === "dark"
    ? theme.palette.background.paper
    : PV_EST_HEADER_BACKGROUND_LIGHT;

export const PV_EST_BORDER = "1px solid rgba(170, 180, 198, 0.6)";

export const PV_EST_HEADER_HEIGHT = 66;
export const PV_EST_ROW_HEIGHT = 40;

export const PV_EST_HEADER_FONT = "clamp(8px, 0.75vw, 14px)";
export const PV_EST_BODY_FONT = "clamp(8px, 0.72vw, 13px)";

export const pvEstimationHeadCellSx = {
  boxSizing: "border-box",

  height: `${PV_EST_HEADER_HEIGHT}px !important`,
  minHeight: `${PV_EST_HEADER_HEIGHT}px !important`,
  maxHeight: `${PV_EST_HEADER_HEIGHT}px !important`,

  p: "2px !important",

  backgroundColor: (theme) =>
    `${getPvEstimationHeaderBackground(theme)} !important`,

  borderRight: `${PV_EST_BORDER} !important`,
  borderBottom: `${PV_EST_BORDER} !important`,

  color: "text.primary",

  textAlign: "center !important",
  verticalAlign: "middle",

  fontSize: `${PV_EST_HEADER_FONT} !important`,
  fontWeight: "700 !important",
  lineHeight: "1.1 !important",

  overflow: "hidden",

  "& .Mui-TableHeadCell-Content": {
    width: "100%",
    minWidth: "0 !important",
    maxWidth: "100%",
    height: "100%",

    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",

    overflow: "hidden",
  },

  "& .Mui-TableHeadCell-Content-Labels": {
    width: "100%",
    minWidth: "0 !important",
    maxWidth: "100%",
    height: "100%",

    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",

    overflow: "hidden",
  },

  "& .Mui-TableHeadCell-Content-Wrapper": {
    width: "100% !important",
    minWidth: "0 !important",
    maxWidth: "100% !important",

    whiteSpace: "normal !important",

    textAlign: "center !important",

    overflow: "hidden !important",

    fontSize: `${PV_EST_HEADER_FONT} !important`,
    fontWeight: "700 !important",
    lineHeight: "1.1 !important",
  },

  "& .Mui-TableHeadCell-Content-Wrapper > *": {
    width: "100% !important",
    minWidth: "0 !important",

    margin: "0 !important",

    textAlign: "center !important",
  },

  "& input:not(.MuiInputBase-input)": {
    width: "100%",
    minWidth: "0 !important",
    maxWidth: "100%",

    boxSizing: "border-box",

    padding: "2px 3px",

    fontSize: "inherit",

    textAlign: "center",

    overflow: "hidden",
  },
};

export const pvEstimationBodyCellSx = {
  boxSizing: "border-box",

  height: `${PV_EST_ROW_HEIGHT}px !important`,
  minHeight: `${PV_EST_ROW_HEIGHT}px !important`,
  maxHeight: `${PV_EST_ROW_HEIGHT}px !important`,

  minWidth: "0 !important",

  p: "0 3px !important",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  borderRight: `${PV_EST_BORDER} !important`,
  borderBottom: `${PV_EST_BORDER} !important`,

  textAlign: "center !important",

  overflow: "hidden",

  fontSize: `${PV_EST_BODY_FONT} !important`,
  fontWeight: "500 !important",
  lineHeight: "1.2 !important",

  "& .Mui-TableBodyCell-Content-Wrapper": {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    height: "100%",

    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",

    textAlign: "center !important",

    overflow: "hidden",
    textOverflow: "ellipsis",

    fontSize: `${PV_EST_BODY_FONT} !important`,
    fontWeight: "500 !important",
  },

  "& .MuiInputBase-root": {
    width: "100%",
    minWidth: "0 !important",
    maxWidth: "100%",

    height: "100%",

    fontSize: `${PV_EST_BODY_FONT} !important`,

    overflow: "hidden",
  },

  "& .MuiSelect-select": {
    width: "100%",
    minWidth: "0 !important",
    maxWidth: "100% !important",

    boxSizing: "border-box",

    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "center !important",

    textAlign: "center !important",

    overflow: "hidden !important",
    textOverflow: "ellipsis",

    fontSize: `${PV_EST_BODY_FONT} !important`,
  },

  "& .MuiInputBase-input": {
    width: "100%",
    minWidth: "0 !important",

    textAlign: "center !important",

    fontSize: `${PV_EST_BODY_FONT} !important`,
  },
};

export const pvEstimationTableProps = {
  layoutMode: "grid-no-grow",

  enableTopToolbar: false,
  enableColumnActions: false,

  defaultColumn: {
    size: 1,
    minSize: 1,
    maxSize: 100000,
    grow: false,
  },

  muiTablePaperProps: {
    elevation: 0,

    sx: {
      width: "100%",
      minWidth: 0,
      maxWidth: "100%",

      overflowX: "hidden",

      backgroundColor: "transparent",
    },
  },

  muiTableContainerProps: {
    sx: {
      width: "100% !important",
      minWidth: "0 !important",
      maxWidth: "100% !important",

      overflowX: "hidden !important",

      "& .MuiTable-root": {
        width: "100% !important",
        minWidth: "0 !important",
        maxWidth: "100% !important",
      },

      "& .MuiTableHead-root, & .MuiTableBody-root": {
        width: "100% !important",
        minWidth: "0 !important",
        maxWidth: "100% !important",
      },

      "& .MuiTableRow-root": {
        width: "100% !important",
        minWidth: "0 !important",
        maxWidth: "100% !important",

        display: "flex !important",

        overflow: "hidden !important",
      },

      "& .MuiTableCell-root": {
        minWidth: "0 !important",
        maxWidth: "none !important",

        boxSizing: "border-box",

        overflow: "hidden",
      },

      "& .MuiTableHead-root .MuiTableCell-root": {
        backgroundColor: (theme) =>
          `${getPvEstimationHeaderBackground(theme)} !important`,

        borderRight: `${PV_EST_BORDER} !important`,
        borderBottom: `${PV_EST_BORDER} !important`,
      },

      "& .MuiTableBody-root .MuiTableCell-root": {
        borderRight: `${PV_EST_BORDER} !important`,
        borderBottom: `${PV_EST_BORDER} !important`,

        textAlign: "center !important",

        fontSize: `${PV_EST_BODY_FONT} !important`,
      },

      "& .MuiTableBody-root .Mui-TableBodyCell-Content-Wrapper": {
        width: "100%",

        display: "flex !important",
        alignItems: "center !important",
        justifyContent: "center !important",

        textAlign: "center !important",

        fontSize: `${PV_EST_BODY_FONT} !important`,
      },
    },
  },

  muiTableHeadRowProps: {
    sx: {
      height: `${PV_EST_HEADER_HEIGHT}px !important`,
      minHeight: `${PV_EST_HEADER_HEIGHT}px !important`,
      maxHeight: `${PV_EST_HEADER_HEIGHT}px !important`,
      backgroundColor: (theme) =>
        `${getPvEstimationHeaderBackground(theme)} !important`,
    },
  },

  muiTableBodyRowProps: {
    sx: {
      height: `${PV_EST_ROW_HEIGHT}px !important`,
      minHeight: `${PV_EST_ROW_HEIGHT}px !important`,
      maxHeight: `${PV_EST_ROW_HEIGHT}px !important`,
    },
  },

  muiTableHeadCellProps: {
    sx: pvEstimationHeadCellSx,
  },

  muiTableBodyCellProps: {
    sx: pvEstimationBodyCellSx,
  },
};

/**
 * Important because some column definitions may contain
 * their own muiTableHeadCellProps / muiTableBodyCellProps.
 * This applies the final styling at column level too.
 */
export function withPvEstimationStyles(columns = []) {
  return columns.map((column) => ({
    ...column,

    minSize: 1,
    maxSize: 100000,

    grow: column.grow !== undefined ? column.grow : true,

    muiTableHeadCellProps: {
      ...(column.muiTableHeadCellProps || {}),

      sx: {
        ...(column.muiTableHeadCellProps?.sx || {}),

        ...pvEstimationHeadCellSx,
      },
    },

    muiTableBodyCellProps: {
      ...(column.muiTableBodyCellProps || {}),

      sx: {
        ...(column.muiTableBodyCellProps?.sx || {}),

        ...pvEstimationBodyCellSx,
      },
    },
  }));
}

const getColumnId = (column) => String(column?.id || column?.accessorKey || "");

export function useResponsivePvEstimationColumns({
  columns = [],
  columnWeights = {},
  actionsWeight = 0,
}) {
  const viewportRef = useRef(null);

  const [tableWidth, setTableWidth] = useState(1);

  useLayoutEffect(() => {
    const element = viewportRef.current;

    if (!element) {
      return undefined;
    }

    let frameId = null;

    const updateWidth = () => {
      cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        const rect = element.getBoundingClientRect();

        /*
         * Important:
         * Some parent containers in the PV page stop shrinking
         * before the actual browser viewport becomes small.
         *
         * Therefore we do NOT trust rect.width alone.
         * The table width must never exceed the actually visible
         * viewport width from its current left position.
         */
        const viewportWidth =
          window.visualViewport?.width || window.innerWidth || rect.width || 1;

        const leftOffset = Math.max(0, rect.left);

        const visibleWidth = Math.max(1, viewportWidth - leftOffset - 8);

        const nextWidth = Math.min(rect.width || 1, visibleWidth);

        setTableWidth(Math.max(1, Math.floor(nextWidth)));
      });
    };

    updateWidth();

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateWidth)
        : null;

    observer?.observe(element);

    window.addEventListener("resize", updateWidth);

    window.visualViewport?.addEventListener("resize", updateWidth);

    return () => {
      cancelAnimationFrame(frameId);

      observer?.disconnect();

      window.removeEventListener("resize", updateWidth);

      window.visualViewport?.removeEventListener("resize", updateWidth);
    };
  }, []);

  const result = useMemo(() => {
    const safeColumns = Array.isArray(columns) ? columns : [];

    const totalColumnWeight = safeColumns.reduce((total, column) => {
      const id = getColumnId(column);

      return total + (columnWeights[id] ?? 1);
    }, 0);

    const totalWeight = totalColumnWeight + actionsWeight;

    /*
     * Small safety allowance for borders /
     * browser sub-pixel rounding.
     */
    const usableWidth = Math.max(1, tableWidth - 2);

    const columnSizing = {};

    const responsiveColumns = safeColumns.map((column) => {
      const id = getColumnId(column);

      const weight = columnWeights[id] ?? 1;

      const width =
        totalWeight > 0 ? Math.max(1, (weight / totalWeight) * usableWidth) : 1;

      if (id) {
        columnSizing[id] = width;
      }

      return {
        ...column,

        size: width,
        minSize: width,
        maxSize: width,
        grow: false,

        muiTableHeadCellProps: {
          ...(column.muiTableHeadCellProps || {}),

          sx: {
            ...(column.muiTableHeadCellProps?.sx || {}),

            ...pvEstimationHeadCellSx,

            width: `${width}px !important`,

            minWidth: `${width}px !important`,

            maxWidth: `${width}px !important`,

            flex: `0 0 ${width}px !important`,

            flexBasis: `${width}px !important`,

            flexGrow: "0 !important",

            flexShrink: "0 !important",
          },
        },

        muiTableBodyCellProps: {
          ...(column.muiTableBodyCellProps || {}),

          sx: {
            ...(column.muiTableBodyCellProps?.sx || {}),

            ...pvEstimationBodyCellSx,

            width: `${width}px !important`,

            minWidth: `${width}px !important`,

            maxWidth: `${width}px !important`,

            flex: `0 0 ${width}px !important`,

            flexBasis: `${width}px !important`,

            flexGrow: "0 !important",

            flexShrink: "0 !important",
          },
        },
      };
    });

    const actionsWidth =
      actionsWeight > 0 && totalWeight > 0
        ? Math.max(1, (actionsWeight / totalWeight) * usableWidth)
        : 0;

    if (actionsWidth > 0) {
      columnSizing["mrt-row-actions"] = actionsWidth;
    }

    return {
      responsiveColumns,
      actionsWidth,
      columnSizing,
    };
  }, [columns, columnWeights, actionsWeight, tableWidth]);

  return {
    viewportRef,
    tableWidth,
    ...result,
  };
}
